import React, { useState, useEffect } from 'react';
import { getUserMatchBets, Match } from '../../api/matchesApi';
import { getTeams, Team } from '../../api/teamsApi';
import Layout from '../../components/Layout';
import { Trophy } from 'lucide-react';
import MatchCard from '../../components/MatchCard';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { format } from 'date-fns';
import { Margin } from '@mui/icons-material';
import { usePlaceMatchBet } from '../../hooks/usePlaceMatchBet';

// Rounds for knockout stage
const ROUNDS = {
  ROUND_OF_16: 'ROUND_OF_16',
  QUARTER_FINAL: 'QUARTER_FINAL',
  SEMI_FINAL: 'SEMI_FINAL',
  FINAL: 'FINAL',
  THIRD_PLACE: 'THIRD_PLACE',
};

// Interface to represent a match in the bracket
// interface BracketMatch {
//   id: string;
//   status: string;
//   homeTeamId: number | null;
//   awayTeamId: number | null;
//   homeTeamName: string;
//   awayTeamName: string;
//   homeScore: number | null;
//   awayScore: number | null;
//   qualifierTeamId: number | null | undefined; // Which team qualifies (user's bet)
//   round: string;
//   nextMatchId: string | null;
//   position: number;
//   homeTeamBetId: number | null;
//   awayTeamBetId: number | null;
//   matchDate: string;
//   userBetWinner: number | null | undefined;
//   // Added properties for match result tracking
//   actualHomeScore: number | null;
//   actualAwayScore: number | null;
//   points: number | null;
//   score_success: boolean | null;
//   result_success: boolean | null;
//   // result: string; // 1/X/2 result
// }

const KnockoutBets: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Use shared hook for placing bets
  const { placeBet, isSubmitting } = usePlaceMatchBet({
    onSuccess: (matchId, updatedMatch) => {
      // Update matches list to reflect the new bet
      setMatches(prev => prev.map(m => {
        if (m.id === matchId) {
          return {
            ...m,
            user_bet_home_score: updatedMatch.user_bet_home_score,
            user_bet_away_score: updatedMatch.user_bet_away_score,
            user_bet_winner: updatedMatch.user_bet_winner
          };
        }
        return m;
      }));
      
      // Update bracket structure with the new data
      const updatedMatches = matches.map(m => {
        if (m.id === matchId) {
          return {
            ...m,
            user_bet_home_score: updatedMatch.user_bet_home_score,
            user_bet_away_score: updatedMatch.user_bet_away_score,
            user_bet_winner: updatedMatch.user_bet_winner
          };
        }
        return m;
      });
      processMatchesIntoBracket(updatedMatches);
    },
    onError: (err) => {
      console.error('Error placing bet:', err);
    }
  });
  
  // Bracket state
  const [roundOf16Matches, setRoundOf16Matches] = useState<Match[]>([]);
  const [quarterFinalMatches, setQuarterFinalMatches] = useState<Match[]>([]);
  const [semiFinalMatches, setSemiFinalMatches] = useState<Match[]>([]);
  const [finalMatch, setFinalMatch] = useState<Match | null>(null);
  const [thirdPlaceMatch, setThirdPlaceMatch] = useState<Match | null>(null);
  const [champion, setChampion] = useState<string | null>(null);
  
  // User's betting selections
  const [betSelections, setBetSelections] = useState<Record<string, {
    homeScore: string;
    awayScore: string;
    qualifierTeamId: number | null | undefined;
    dirty?: boolean;
  }>>({});

  // Fetch data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [matchesData, teamsData] = await Promise.all([
          getUserMatchBets(), // Use existing API
          getTeams()
        ]);
        
        // Filter to only knockout stage matches
        const knockoutMatches = matchesData.filter(match => 
          match.round === ROUNDS.ROUND_OF_16 ||
          match.round === ROUNDS.QUARTER_FINAL ||
          match.round === ROUNDS.SEMI_FINAL ||
          match.round === ROUNDS.FINAL ||
          match.round === ROUNDS.THIRD_PLACE
        );
        
        setMatches(knockoutMatches);
        setTeams(teamsData);
        
        // Process matches into bracket structure
        processMatchesIntoBracket(knockoutMatches);
        
        // Initialize bet selections from user's existing bets
        const initialSelections: Record<string, {
          homeScore: string;
          awayScore: string;
          qualifierTeamId: number | null | undefined;
        }> = {};
        
        knockoutMatches.forEach(match => {
          // Get user's existing bet for this match if any
          initialSelections[match.id] = {
            homeScore: match.user_bet_home_score !== null && match.user_bet_home_score !== undefined
              ? match.user_bet_home_score.toString()
              : '',
            awayScore: match.user_bet_away_score !== null && match.user_bet_away_score !== undefined
              ? match.user_bet_away_score.toString()
              : '',
            qualifierTeamId: determineQualifierFromUserBet(match)
          };
        });
        
        setBetSelections(initialSelections);
        setError(null);
      } catch (err) {
        console.error('Error fetching knockout data:', err);
        setError('Failed to load knockout matches. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Determine qualifier team from user's bet result
  const determineQualifierFromUserBet = (match: Match): number | null | undefined => {
    // If no user bet or no teams, return null
    if (match.user_bet_home_score === undefined || match.user_bet_away_score === undefined) {
      return match.user_bet_winner;
    }
    
    return match.user_bet_winner
    // const homeScore = match.user_bet_home_score;
    // const awayScore = match.user_bet_away_score;
    
    // // If scores are missing, return null
    // if (homeScore === null || awayScore === null) {
    //   return null;
    // }
    
    // // Determine qualifier based on scores
    // if (homeScore > awayScore) {
    //   return match.home_team_id || null;
    // } else if (homeScore < awayScore) {
    //   return match.away_team_id || null;
    // }
    
    // // If it's a draw, check for a qualifier_team_id field or fall back to match.result
    // if (match.qualifier_team_id) {
    //   return match.qualifier_team_id;
    // }
    
    // // Check result field for 1/X/2
    // if (match.result === '1') {
    //   return match.home_team_id || null;
    // } else if (match.result === '2') {
    //   return match.away_team_id || null;
    // }
    
    // return null;
  };

  // Process matches into bracket structure
  const processMatchesIntoBracket = (matchesData: Match[]) => {
    // Create a map of match ID to match for easy lookup
    const matchMap: Record<string, Match> = {};
    matchesData.forEach(match => {
      matchMap[match.id] = match;
    });
    
    // Sort matches by round
    const roundOf16 = matchesData
      .filter(m => m.round === ROUNDS.ROUND_OF_16)
      .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    
    const quarterFinals = matchesData
      .filter(m => m.round === ROUNDS.QUARTER_FINAL)
      .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    
    const semiFinals = matchesData
      .filter(m => m.round === ROUNDS.SEMI_FINAL)
      .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    
    const final = matchesData.find(m => m.round === ROUNDS.FINAL);
    
    const thirdPlace = matchesData.find(m => m.round === ROUNDS.THIRD_PLACE);
    
    // Build a match progression map to track which teams advance
    const getQualifyingTeamForMatch = (match: Match): number | null => {
      // If user has already placed a bet on this match
      if (match.user_bet_home_score !== undefined && match.user_bet_away_score !== undefined) {
        if (match.user_bet_home_score === null || match.user_bet_away_score === null) {
          return null;
        }
        
        // Determine qualifier based on user's bet
        if (match.user_bet_home_score > match.user_bet_away_score) {
          return match.home_team_id;
        } else if (match.user_bet_home_score < match.user_bet_away_score) {
          return match.away_team_id;
        } else if (match.winner_id) {
          // For draws, use the qualifier
          return match.winner_id;
        }
      }
      
      // Fallback to actual match result if available
      if (match.home_team_score !== null && match.away_team_score !== null) {
        if (match.home_team_score > match.away_team_score) {
          return match.home_team_id;
        } else if (match.home_team_score < match.away_team_score) {
          return match.away_team_id;
        }
      }
      
      return null;
    };
    
    // Find the qualifying teams from previous rounds
    const qualifyingTeams: Record<string, number | null> = {};
    
    // Process round of 16 first
    roundOf16.forEach(match => {
      if (match.knockout_next_match) {
        qualifyingTeams[match.knockout_next_match] = getQualifyingTeamForMatch(match);
      }
    });
    
    // Process quarter finals
    quarterFinals.forEach(match => {
      if (match.knockout_next_match) {
        qualifyingTeams[match.knockout_next_match] = getQualifyingTeamForMatch(match);
      }
    });
    
    // Process semi finals
    semiFinals.forEach(match => {
      if (match.knockout_next_match) {
        qualifyingTeams[match.knockout_next_match] = getQualifyingTeamForMatch(match);
      }
    });
    
    // Helper function to apply user's predicted teams to matches where teams aren't yet decided
    const applyUserPredictedTeams = (match: Match): Match => {
      const modifiedMatch = { ...match };
      
      // Check if home team is missing but we have a user prediction
      if (!modifiedMatch.home_team_id && modifiedMatch.id in qualifyingTeams) {
        const predictedHomeTeamId = qualifyingTeams[modifiedMatch.id];
        if (predictedHomeTeamId) {
          modifiedMatch.home_team_id = predictedHomeTeamId;
          const team = teams.find(t => t.id === predictedHomeTeamId);
          if (team) {
            modifiedMatch.home_team = team;
          }
        }
      }
      
      // Check if away team is missing but we have a user prediction
      if (!modifiedMatch.away_team_id && modifiedMatch.id in qualifyingTeams) {
        const predictedAwayTeamId = qualifyingTeams[modifiedMatch.id];
        if (predictedAwayTeamId) {
          modifiedMatch.away_team_id = predictedAwayTeamId;
          const team = teams.find(t => t.id === predictedAwayTeamId);
          if (team) {
            modifiedMatch.away_team = team;
          }
        }
      }
      
      return modifiedMatch;
    };
    
    // Apply predicted teams to matches where teams aren't decided yet
    const enhancedQuarterFinals = quarterFinals.map(applyUserPredictedTeams).sort((a, b) => a.id.localeCompare(b.id));
    const enhancedSemiFinals = semiFinals.map(applyUserPredictedTeams);
    const enhancedFinal = final ? applyUserPredictedTeams(final) : null;
    const enhancedThirdPlace = thirdPlace ? applyUserPredictedTeams(thirdPlace) : null;
    
    // Convert to bracket matches
    const round16Bracket = roundOf16.sort((a, b) => a.id.localeCompare(b.id));
    const quarterBracket = enhancedQuarterFinals;
    const semiBracket = enhancedSemiFinals;
    const finalBracket = enhancedFinal;
    const thirdPlaceBracket = enhancedThirdPlace;
    
    setRoundOf16Matches(round16Bracket);
    setQuarterFinalMatches(quarterBracket);
    setSemiFinalMatches(semiBracket);
    setFinalMatch(finalBracket);
    setThirdPlaceMatch(thirdPlaceBracket);
    
    // Determine champion if final match has a qualifier
    if (enhancedFinal) {
      const qualifierId = getQualifyingTeamForMatch(enhancedFinal);
      if (qualifierId) {
        const team = teams.find(t => t.id === qualifierId);
        if (team) {
          setChampion(team.name);
        }
      }
    }
  };
  
  // Handle score input change
  const handleScoreChange = (matchId: string, team: 'home' | 'away', value: string) => {
    // Ensure the value is a non-negative number
    if (value !== '' && !/^\d+$/.test(value)) {
      return;
    }
    
    setBetSelections(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [team === 'home' ? 'homeScore' : 'awayScore']: value,
        dirty: true
      }
    }));
  };
  
  // Handle qualifier selection
  const handleQualifierChange = (matchId: string, teamId: number | null) => {
    setBetSelections(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        qualifierTeamId: teamId,
        dirty: true
      }
    }));
  };
  
  // Translate to 1/X/2 result
  // const determineResult = (homeScore: number, awayScore: number, qualifierTeamId: number | null, match: Match): string => {
  //   if (homeScore > awayScore) return '1';
  //   if (homeScore < awayScore) return '2';
    
  //   // For a draw in knockout, we need to determine the qualifier
  //   if (qualifierTeamId === match.home_team_id) return '1';
  //   if (qualifierTeamId === match.away_team_id) return '2';
    
  //   return 'X'; // Default to draw if no qualifier selected
  // };
  
  // Auto-place bet when valid and dirty
  useEffect(() => {
    Object.entries(betSelections).forEach(([matchId, selection]) => {
      if (!selection.dirty) return;
      if (selection.homeScore === '' || selection.awayScore === '') return;
      const homeScore = parseInt(selection.homeScore, 10);
      const awayScore = parseInt(selection.awayScore, 10);
      if (isNaN(homeScore) || isNaN(awayScore) || homeScore < 0 || awayScore < 0) return;
      // Find the match
      const match = matches.find(m => m.id === matchId);
      if (!match) return;
      // For tied scores, ensure a qualifier is selected
      if (homeScore === awayScore && !selection.qualifierTeamId) return;
      
      // Calculate qualifier for knockout matches
      let qualifier = selection.qualifierTeamId;
      if (homeScore > awayScore) qualifier = match.home_team_id;
      else if (homeScore < awayScore) qualifier = match.away_team_id;
      
      // Place the bet using the shared hook
      placeBet(
        matchId,
        selection.homeScore,
        selection.awayScore,
        qualifier ?? null,
        match
      ).then(() => {
        // Reset dirty flag after successful bet
        setBetSelections(prev => ({
          ...prev,
          [matchId]: {
            ...prev[matchId],
            dirty: false
          }
        }));
      }).catch((err) => {
        console.error('Error placing bet:', err);
      });
    });
  }, [betSelections, matches, placeBet]);
  
  // Helper function to handle bet placement from MatchCard
  const handlePlaceBet = async (matchId: string, homeScore: string, awayScore: string, winnerId?: number | null) => {
    const match = matches.find(m => m.id === matchId);
    if (match) {
      await placeBet(matchId, homeScore, awayScore, winnerId, match);
    }
  };

  // Get team name by ID
  const getTeamName = (teamId: number | null): string => {
    if (!teamId) return '';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : '';
  };
  
  // Get team logo by ID
  const getTeamLogo = (teamId: number | null): string => {
    if (!teamId) return '';
    const team = teams.find(t => t.id === teamId);
    return team ? team.logo_url : '';
  };
  
  // Renders a single match in the bracket
  // const Match = ({ match, round }: { match: Match, round: string }) => {
  //   const homeTeamId = match.home_team_id;
  //   const awayTeamId = match.away_team_id;
  //   const homeTeamName = homeTeamId ? getTeamName(homeTeamId) : match.home_team_placeholder;
  //   const awayTeamName = awayTeamId ? getTeamName(awayTeamId) : match.away_team_placeholder;
  //   const homeTeamLogo = homeTeamId ? getTeamLogo(homeTeamId) : '';
  //   const awayTeamLogo = awayTeamId ? getTeamLogo(awayTeamId) : '';
    
  //   const selection = betSelections[match.id] || {
  //     homeScore: '',
  //     awayScore: '',
  //     qualifierTeamId: null
  //   };
    
  //   const isSubmitting = submitting[match.id] || false;
    
  //   // Format match date for display
  //   const matchDate = match.matchDate 
  //     ? format(new Date(match.matchDate), 'dd/MM/yy • HH:mm') 
  //     : '';
    
  //   // Check if match has been played
  //   const isMatchStarted = match.status != 'SCHEDULED';
    
  //   // Get status text based on match played state
  //   const statusText = match.status == 'COMPLETED' ? "הסתיים" : match.status == 'LIVE' ? 'חי' : "עתידי";
    
  //   // Get status color based on match played state
  //   const statusColor = isMatchStarted 
  //     ? "text-blue-500 bg-blue-900/30" 
  //     : "text-gray-400 bg-gray-800/50";
    
  //   // Get the round name in Hebrew
  //   const roundName = (() => {
  //     switch(round) {
  //       case ROUNDS.ROUND_OF_16:
  //         return "שמינית גמר";
  //       case ROUNDS.QUARTER_FINAL:
  //         return "רבע גמר";
  //       case ROUNDS.SEMI_FINAL:
  //         return "חצי גמר";
  //       case ROUNDS.FINAL:
  //         return "גמר";
  //       case ROUNDS.THIRD_PLACE:
  //         return "מקום שלישי";
  //       default:
  //         return round;
  //     }
  //   })();
    
  //   function showUserBetWinner(
  //     teamId: number | null | undefined,
  //     userBetHomeScore: number | null | undefined,
  //     userBetAwayScore: number | null | undefined,
  //     userBetWinner: number | null | undefined
  //   ): string {
  //     return userBetHomeScore === userBetAwayScore && userBetWinner !== null && userBetWinner === teamId ? '*' : '';
  //   }

  //   return (
  //     <div className="bg-gray-900 border border-gray-700 rounded p-2 w-full" style={{ minWidth: 350 }} dir="rtl">
  //       {/* Header - RTL layout matching the MatchCard component */}
  //       <div className="bg-gray-800 px-3 py-2 flex justify-between items-center">
  //         {/* Right side: round and status/date */}
  //         <div className="flex items-center">
  //           <span className="text-gray-400 text-xs ml-2">{roundName}</span>
  //           {!isMatchStarted ? (
  //             <span className="text-xs">{matchDate}</span>
  //           ) : (
  //             <span className={`text-xs font-bold ${statusColor} px-2 py-1 rounded`}>
  //               {statusText}
  //             </span>
  //           )}
  //         </div>
          
  //         {/* Left side: points and success indicators */}
  //         {isMatchStarted && (
  //           <div className="flex items-center space-x-2">
  //             <div className="flex items-center ml-3" title="ניחוש תוצאה מדויקת">
  //               <ScoreboardIcon 
  //                 fontSize="small" 
  //                 className={match.score_success ? "text-green-500" : "text-red-500"} 
  //               />
  //             </div>
              
  //             <div className="flex items-center ml-2" title="כיוון">
  //               <SyncAltIcon 
  //                 fontSize="small"
  //                 className={match.result_success ? "text-green-500" : "text-red-500"} 
  //               />
  //             </div>
              
  //             <div className="flex items-center bg-gray-800 px-2 py-0.5 rounded ml-2">
  //               <span className="text-xs font-medium ml-1">נק׳:</span>
  //               <span className="text-sm text-green-500 font-bold">{match.points || 0}</span>
  //             </div>
  //           </div>
  //         )}
  //       </div>
        
  //       <div className="flex flex-col gap-2 p-3">
  //         {/* Home team */}
  //         <div className="flex items-center justify-between">
  //           <div className="flex items-center">
  //             {homeTeamLogo && (
  //               <img src={homeTeamLogo} alt={homeTeamName} className="h-5 w-5 ml-2" />
  //             )}
  //             <span className="font-medium">{homeTeamName || '?'}</span>
  //           </div>
            
  //           <div className="flex items-center">
  //             {isMatchStarted ? (
  //               <>
  //                 <span className="text-lg font-bold ml-2">{match.actualHomeScore}</span>
  //                 <span className="text-gray-400 ml-2">({match.homeScore})
  //                   {showUserBetWinner(match.homeTeamId, match.homeScore, match.awayScore, match.userBetWinner)}</span>
  //               </>
  //             ) : (
  //               <input 
  //                 type="text" 
  //                 className="w-12 bg-gray-800 text-white border border-gray-700 rounded p-1 ml-2 text-center"
  //                 value={selection.homeScore}
  //                 onChange={(e) => handleScoreChange(match.id, 'home', e.target.value)}
  //                 placeholder="-"
  //               />
  //             )}
  //           </div>
  //         </div>
          
  //         {/* Away team */}
  //         <div className="flex items-center justify-between border-t border-gray-800 pt-2">
  //           <div className="flex items-center">
  //             {awayTeamLogo && (
  //               <img src={awayTeamLogo} alt={awayTeamName} className="h-5 w-5 ml-2" />
  //             )}
  //             <span className="font-medium">{awayTeamName || '?'}</span>
  //           </div>
            
  //           <div className="flex items-center">
  //             {isMatchStarted ? (
  //               <>
  //                 <span className="text-lg font-bold ml-2">{match.actualAwayScore}</span>
  //                 <span className="text-gray-400 ml-2">({match.awayScore})
  //                 {showUserBetWinner(match.awayTeamId, match.homeScore, match.awayScore, match.userBetWinner)}
  //                 </span>
  //               </>
  //             ) : (
  //               <input 
  //                 type="text" 
  //                 className="w-12 bg-gray-800 text-white border border-gray-700 rounded p-1 ml-2 text-center"
  //                 value={selection.awayScore}
  //                 onChange={(e) => handleScoreChange(match.id, 'away', e.target.value)}
  //                 placeholder="-"
  //               />
  //             )}
  //           </div>
  //         </div>
          
  //         {/* For a draw, select qualifier - only show for upcoming matches */}
  //         {!isMatchStarted && selection.homeScore && selection.awayScore && 
  //          selection.homeScore === selection.awayScore && (
  //           <div className="mt-2">
  //             <div className="text-xs text-yellow-500 mb-1">בחר קבוצה מעפילה:</div>
  //             <div className="flex space-x-2">
  //               {homeTeamId && (
  //                 <button
  //                   className={`px-2 py-1 text-xs rounded flex-1 ${
  //                     selection.qualifierTeamId === homeTeamId
  //                       ? 'bg-yellow-600 text-white'
  //                       : 'bg-gray-800 text-gray-300'
  //                   }`}
  //                   onClick={() => handleQualifierChange(match.id, homeTeamId)}
  //                 >
  //                   {homeTeamName}
  //                 </button>
  //               )}
  //               {awayTeamId && (
  //                 <button
  //                   className={`px-2 py-1 text-xs rounded flex-1 ${
  //                     selection.qualifierTeamId === awayTeamId
  //                       ? 'bg-yellow-600 text-white'
  //                       : 'bg-gray-800 text-gray-300'
  //                   }`}
  //                   onClick={() => handleQualifierChange(match.id, awayTeamId)}
  //                 >
  //                   {awayTeamName}
  //                 </button>
  //               )}
  //             </div>
  //           </div>
  //         )}
          
  //         {/* No submit button, auto-bet is handled by useEffect */}
  //       </div>
  //     </div>
  //   );
  // };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-stake-blue"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-500 text-white p-4 rounded-md">
          {error}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">שלב הנוק-אאוט</h1>
        </div>
        
        <div className="overflow-x-auto pb-10 w-screen -mx-4 sm:mx-0">
          <table className="min-w-max">
            <thead>
              <tr>
                <th className="pb-4 text-center text-yellow-500">שמינית גמר</th>
                <th className="pb-4 w-12"></th>
                <th className="pb-4 text-center text-yellow-500">רבע גמר</th>
                <th className="pb-4 w-12"></th>
                <th className="pb-4 text-center text-yellow-500">חצי גמר</th>
                <th className="pb-4 w-12"></th>
                <th className="pb-4 text-center text-yellow-500">מקום שלישי</th>
                <th className="pb-4 w-12"></th>
                <th className="pb-4 text-center text-yellow-500">גמר</th>
              </tr>
            </thead>
            <tbody>
              {/* Row 1 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[0] && (
                    <MatchCard match={roundOf16Matches[0]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={2} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={2} className="align-middle p-1">
                  {quarterFinalMatches[0] && (
                    <MatchCard match={quarterFinalMatches[0]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={4} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={4} className="align-middle p-1">
                  {semiFinalMatches[0] && (
                    <MatchCard match={semiFinalMatches[0]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={8} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={8} className="align-middle p-1">
                  {thirdPlaceMatch && (
                    <MatchCard match={thirdPlaceMatch} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={8} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={8} className="align-middle p-1">
                  {finalMatch && (
                    <MatchCard match={finalMatch} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={8} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
              </tr>
              {/* Row 2 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[1] && (
                    <MatchCard match={roundOf16Matches[1]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 3 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[2] && (
                    <MatchCard match={roundOf16Matches[2]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={2} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={2} className="align-middle p-1">
                  {quarterFinalMatches[1] && (
                    <MatchCard match={quarterFinalMatches[1]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 4 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[3] && (
                    <MatchCard match={roundOf16Matches[3]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 5 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[4] && (
                    <MatchCard match={roundOf16Matches[4]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={2} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={2} className="align-middle p-1">
                  {quarterFinalMatches[2] && (
                    <MatchCard match={quarterFinalMatches[2]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={4} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={4} className="align-middle p-1">
                  {semiFinalMatches[1] && (
                    <MatchCard match={semiFinalMatches[1]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 6 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[5] && (
                    <MatchCard match={roundOf16Matches[5]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 7 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[6] && (
                    <MatchCard match={roundOf16Matches[6]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
                <td rowSpan={2} className="relative">
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-gray-600"></div>
                  </div>
                </td>
                <td rowSpan={2} className="align-middle p-1">
                  {quarterFinalMatches[3] && (
                    <MatchCard match={quarterFinalMatches[3]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
              {/* Row 8 */}
              <tr>
                <td className="align-top p-1">
                  {roundOf16Matches[7] && (
                    <MatchCard match={roundOf16Matches[7]} compact={true} onPlaceBet={handlePlaceBet} />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default KnockoutBets; 